import { LeadModel } from "@blazeo.com/calendar-client";

const ROUTING_SKIP_KEYS = new Set([ "calendarId"]);

const ROUTING_SKIP_TYPES = new Set(["appointmentsetting"]);

const BLAZEO_LEAD_GUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const getBlazeoLeadGuid = (...values) => {
  for (const value of values) {
    const id = String(value || "").trim();
    if (BLAZEO_LEAD_GUID.test(id)) return id;
  }
  return null;
};

/** CRM lead ids are numeric. Activity and appointment APIs need the Blazeo lead GUID. */
export const resolveLiveBlazeoLeadId = async (lead, companyKey) => {
  const existing = getBlazeoLeadGuid(lead?.leadId, lead?.LeadId);
  if (existing) return existing;

  const email = lead?.email || lead?.Email;
  if (!email || !companyKey) return null;

  const blazeoRes = await LeadModel.getByEmail(email, companyKey);
  const blazeoLeads = Array.isArray(blazeoRes)
    ? blazeoRes
    : blazeoRes
      ? [blazeoRes]
      : [];
  const liveLead = blazeoLeads.find((item) => item && !item.isDeleted);
  if (!liveLead) return null;
  return getBlazeoLeadGuid(liveLead.leadId, liveLead.LeadId);
};

function parseSurveyJson(flowJson) {
  if (!flowJson) return null;
  if (typeof flowJson === "object" && flowJson !== null) {
    return flowJson;
  }
  if (typeof flowJson === "string") {
    try {
      return JSON.parse(flowJson);
    } catch {
      return null;
    }
  }
  return null;
}

function collectSurveyQuestions(node, acc = []) {
  if (!node || typeof node !== "object") return acc;

  if (Array.isArray(node.elements)) {
    for (const el of node.elements) {
      if (el.type === "panel" && Array.isArray(el.elements)) {
        collectSurveyQuestions(el, acc);
      } else if (
        el.name &&
        !ROUTING_SKIP_TYPES.has(el.type) &&
        !ROUTING_SKIP_KEYS.has(el.name)
      ) {
        acc.push({
          name: el.name,
          title: el.title || el.name,
          type: el.type,
          choices: el.choices,
        });
      }
    }
  }

  if (Array.isArray(node.pages)) {
    for (const page of node.pages) {
      collectSurveyQuestions(page, acc);
    }
  }

  return acc;
}

function resolveChoiceText(question, answer) {
  const formatted = formatAnswer(answer);
  if (!formatted || !Array.isArray(question.choices)) return formatted;

  const strAnswer = String(answer);
  const match = question.choices.find((choice) => {
    if (typeof choice === "object" && choice !== null) {
      return (
        String(choice.value) === strAnswer || String(choice.text) === strAnswer
      );
    }
    return String(choice) === strAnswer;
  });

  if (match && typeof match === "object") {
    return match.text || formatAnswer(match.value);
  }
  return formatted;
}

function formatQuestionAnswer(question, rawAnswer) {
  if (rawAnswer == null || rawAnswer === "") return null;
  if (question.type === "radiogroup" || question.type === "dropdown") {
    return resolveChoiceText(question, rawAnswer);
  }
  if (question.type === "checkbox" && Array.isArray(rawAnswer)) {
    return rawAnswer
      .map((item) => resolveChoiceText({ ...question, type: "radiogroup" }, item))
      .filter(Boolean)
      .join(", ");
  }
  return formatAnswer(rawAnswer);
}

function parseFlowPath(flowPath) {
  if (!flowPath) return {};
  try {
    return typeof flowPath === "string" ? JSON.parse(flowPath) : flowPath;
  } catch {
    return {};
  }
}

function formatAnswer(value) {
  if (value == null || value === "") return null;
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function humanizeKey(key) {
  return String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

/**
 * Build routing display rows from event flowPath and optional survey flowJson.
 * When flowJson is provided, question titles and choice labels are used.
 */
export function getRoutingRows(flowPath, flowJson = null) {
  const answers = parseFlowPath(flowPath);
  if (!answers || typeof answers !== "object") return [];

  // To handle SurveyJSON format
  const surveyJson = parseSurveyJson(flowJson);
  const questions = surveyJson ? collectSurveyQuestions(surveyJson) : [];

  if (questions.length) {
    return questions
      .map((question) => {
        const answerKey = question.title || question.name;
        const value = formatQuestionAnswer(
          question,
          answers[answerKey] ?? answers[question.name]
        );
        if (!value) return null;
        return {
          key: answerKey,
          label: question.title,
          value,
        };
      })
      .filter(Boolean);
  }

  // To handle flowPath format (simple parsed data)
  return Object.entries(answers)
    .filter(([key, value]) => !ROUTING_SKIP_KEYS.has(key) && formatAnswer(value))
    .map(([key, value]) => ({
      key,
      label: humanizeKey(key),
      value: formatAnswer(value),
    }));
}

async function resolveFlowTitle(dataClient, flowData) {
  const embedded = parseSurveyJson(flowData?.flowJson);
  if (embedded?.title) return String(embedded.title).trim();

  const flowId = flowData?.flowId;
  if (!flowId || typeof dataClient?.flows?.getFlow !== "function") return "";

  try {
    const flow = await dataClient.flows.getFlow({ flowId });
    const title = flow?.title || flow?.name || flow?.Title || flow?.Name;
    if (title) return String(title).trim();
    const survey = parseSurveyJson(flow?.flowJson ?? flow?.flowData ?? flow?.FlowData);
    return String(survey?.title || "").trim();
  } catch (error) {
    console.error("Error loading flow title:", error);
    return "";
  }
}

async function fetchAppointmentFlow(dataClient, appointment) {
  const eventId = appointment?.eventId;
  if (!eventId) {
    return { ...appointment, flowLoaded: true, routingRows: [] };
  }

  try {
    const flowData = await dataClient.appointments.getEventFlow(eventId);
    if (!flowData?.flowPath) {
      return { ...appointment, flowLoaded: true, routingRows: [] };
    }

    const flowTitle = await resolveFlowTitle(dataClient, flowData);
    return {
      ...appointment,
      flowId: flowData.flowId ?? appointment?.flowId,
      flowTitle: flowTitle || null,
      flowPath: flowData.flowPath,
      flowJson: flowData.flowJson ?? null,
      routingRows: getRoutingRows(flowData.flowPath, flowData.flowJson),
      flowLoaded: true,
    };
  } catch (error) {
    console.error("Error loading appointment routing:", error);
    return { ...appointment, flowLoaded: true, routingRows: [] };
  }
}

/**
 * Fetch event flow for each appointment and attach routingRows to the object.
 */
export async function enrichAppointmentsWithFlow(dataClient, appointments = []) {
  if (!appointments?.length) return appointments;

  return Promise.all(
    appointments.map((appointment) => fetchAppointmentFlow(dataClient, appointment))
  );
}
